import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { RiEyeFill, RiPencilFill } from "react-icons/ri";

import TableContainer from "../../BaseComponents/BaseTable";
import BaseModal from "../../BaseComponents/BaseModal";
import BaseInput from "../../BaseComponents/BaseInput";
import BaseSelect from "../../BaseComponents/BaseSelect";
import ActionDropdown from "../../BaseComponents/BaseActionDropdown";
import { usePageLoader } from "../../_helper/loading/LoadingContext";

import { checkStatusCodeSuccess, handleResponse } from "../../Constant/common";
import {
  getInquiryByIdApi,
  listOfInquiriesApi,
  updateInquiryStatusApi,
} from "../../Api/inquiryApi";
import {
  ACCESSOR_KEYS,
  ActionsLabel,
  CompanyNameLabel,
  EmailLabel,
  FullNameLabel,
  INQUIRY_STATUS_OPTIONS,
  InquiryDetailsTitle,
  InquiryLabel,
  MessageLabel,
  ReasonForContactLabel,
  RemarksLabel,
  SearchInquiryPlaceholder,
  StatusLabel,
  SubmitUpdateLabel,
  UpdateInquiryTitle,
  UpdateLabel,
  ViewLabel,
} from "../../Constant/Inquiry";

const shouldShowValue = (value) =>
  value !== null && value !== undefined && !(typeof value === "string" && value.trim() === "");

const InquiryViewField = ({ label, value, col = "col-md-6", pre = false }) => {
  if (!shouldShowValue(value)) return null;
  return (
    <div className={`${col} mb-2`} style={pre ? { whiteSpace: "pre-wrap" } : undefined}>
      <strong>{label}:</strong> {value}
    </div>
  );
};

InquiryViewField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  col: PropTypes.string,
  pre: PropTypes.bool,
};

const InquiryActionsContext = React.createContext(null);

const useInquiryActions = () => {
  const ctx = React.useContext(InquiryActionsContext);
  if (!ctx) throw new Error("useInquiryActions must be used within InquiryActionsContext.Provider");
  return ctx;
};

const InquiryActionsCell = ({ inquiry, openActionId, setOpenActionId, onView, onUpdate }) => {
  const isOpen = openActionId === inquiry.id;
  const actions = [
    {
      label: ViewLabel,
      icon: RiEyeFill,
      className: "text-success",
      onClick: () => {
        onView(inquiry.id);
        setOpenActionId(null);
      },
    },
    {
      label: UpdateLabel,
      icon: RiPencilFill,
      className: "text-primary",
      onClick: () => {
        onUpdate(inquiry);
        setOpenActionId(null);
      },
    },
  ];

  return (
    <ActionDropdown
      isOpen={isOpen}
      onToggle={() => setOpenActionId(isOpen ? null : inquiry.id)}
      onClose={() => setOpenActionId(null)}
      actions={actions}
    />
  );
};

InquiryActionsCell.propTypes = {
  inquiry: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    status: PropTypes.string,
    remarks: PropTypes.string,
  }).isRequired,
  openActionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setOpenActionId: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
};

const InquiryActionsCellRenderer = ({ row }) => {
  const { openActionId, setOpenActionId, onView, onUpdate } = useInquiryActions();
  return (
    <InquiryActionsCell
      inquiry={row.original}
      openActionId={openActionId}
      setOpenActionId={setOpenActionId}
      onView={onView}
      onUpdate={onUpdate}
    />
  );
};

InquiryActionsCellRenderer.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.object.isRequired,
  }).isRequired,
};

const Inquiry = () => {
  useEffect(() => {
    document.title = InquiryLabel;
  }, []);

  const { setLoading } = usePageLoader();

  const [inquiries, setInquiries] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalNumberOfRows, setTotalNumberOfRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [customPageSize, setCustomPageSize] = useState(5);
  const [searchValue, setSearchValue] = useState("");

  const [sorting, setSorting] = useState({ orderBy: "id", direction: "desc" });
  const [openActionId, setOpenActionId] = useState(null);

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewData, setViewData] = useState(null);

  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateForm, setUpdateForm] = useState({
    id: null,
    status: INQUIRY_STATUS_OPTIONS[0]?.value,
    remarks: "",
  });

  const statusOptions = useMemo(() => INQUIRY_STATUS_OPTIONS, []);

  const handleFetchSorting = (page, columnName, order) => {
    setSorting({ orderBy: columnName, direction: order?.toLowerCase() });
    setCurrentPage(page);
  };

  const fetchInquiryList = useCallback(
    async (page = currentPage) => {
      const payload = {
        search: searchValue,
        order: [sorting.orderBy, sorting.direction],
        pageSize: customPageSize,
        pageNumber: page,
      };

      setLoading(true);
      try {
        const response = await listOfInquiriesApi(payload);
        if (checkStatusCodeSuccess(response?.statusCode)) {
          setInquiries(response?.data?.listOfInquiries || []);
          setTotalPages(response?.data?.totalPages || 1);
          setTotalRecords(response?.data?.totalRecordsCount || 0);
          setTotalNumberOfRows(response?.data?.numberOfRows || 0);
          setCurrentPage(response?.data?.currentPage || page);
        } else {
          setInquiries([]);
        }
      } catch {
        setInquiries([]);
      } finally {
        setLoading(false);
      }
    },
    [currentPage, customPageSize, searchValue, sorting, setLoading],
  );

  useEffect(() => {
    Promise.resolve().then(() => fetchInquiryList());
  }, [fetchInquiryList]);

  const handleView = useCallback(async (id) => {
    setLoading(true);
    try {
      const resp = await getInquiryByIdApi({ id });
      if (checkStatusCodeSuccess(resp?.statusCode)) {
        setViewData(resp?.data || null);
        setViewModalOpen(true);
      } else {
        toast.error(resp?.message || handleResponse.somethingWrong);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const openUpdateModal = useCallback((row) => {
    setUpdateForm({
      id: row?.id ?? null,
      status: row?.status || INQUIRY_STATUS_OPTIONS[0]?.value,
      remarks: row?.remarks || "",
    });
    setUpdateModalOpen(true);
  }, []);

  const handleUpdateSubmit = async () => {
    if (!updateForm?.id) return;

    setUpdateLoading(true);
    try {
      const resp = await updateInquiryStatusApi({
        id: updateForm.id,
        status: updateForm.status,
        remarks: updateForm.remarks || null,
      });
      if (checkStatusCodeSuccess(resp?.statusCode)) {
        toast.success(resp?.message);
        setUpdateModalOpen(false);
        await fetchInquiryList(1);
      } else {
        toast.error(resp?.message || handleResponse.somethingWrong);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message);
    } finally {
      setUpdateLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: ACCESSOR_KEYS.FULL_NAME,
        header: FullNameLabel,
        accessorKey: ACCESSOR_KEYS.FULL_NAME,
        enableColumnFilter: false,
      },
      {
        id: ACCESSOR_KEYS.COMPANY_NAME,
        header: CompanyNameLabel,
        accessorKey: ACCESSOR_KEYS.COMPANY_NAME,
        enableColumnFilter: false,
      },
      {
        id: ACCESSOR_KEYS.EMAIL,
        header: EmailLabel,
        accessorKey: ACCESSOR_KEYS.EMAIL,
        enableColumnFilter: false,
      },
      {
        id: ACCESSOR_KEYS.STATUS,
        header: StatusLabel,
        accessorKey: ACCESSOR_KEYS.STATUS,
        enableColumnFilter: false,
        cell: ({ row }) => row?.original?.status ?? "-",
      },
      {
        id: ACCESSOR_KEYS.ACTIONS,
        header: ActionsLabel,
        enableSorting: false,
        cell: InquiryActionsCellRenderer,
      },
    ],
    [],
  );

  const actionsContextValue = useMemo(
    () => ({
      openActionId,
      setOpenActionId,
      onView: handleView,
      onUpdate: openUpdateModal,
    }),
    [openActionId, handleView, openUpdateModal],
  );

  return (
    <div className="container">
      <div className="d-flex justify-content-between align-items-center">
        <h5>{InquiryLabel}</h5>
      </div>

      <div className="card border-0 mt-2 p-2">
        <InquiryActionsContext.Provider value={actionsContextValue}>
          <TableContainer
            totalPages={totalPages}
            totalRecords={totalRecords}
            totalNumberOfRows={totalNumberOfRows}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            fetchData={setCurrentPage}
            customPageSize={customPageSize}
            setCustomPageSize={setCustomPageSize}
            columns={columns}
            fetchSortingData={handleFetchSorting}
            data={inquiries}
            divClass="table-responsive mb-1"
            shouldLineBreak={true}
            onSearch={(val) => {
              setCurrentPage(1);
              setSearchValue(val);
            }}
            manualPagination
            manualFiltering
            SearchPlaceholder={SearchInquiryPlaceholder}
            isGlobalFilter={true}
          />
        </InquiryActionsContext.Provider>
      </div>

      <BaseModal
        isOpen={viewModalOpen}
        title={InquiryDetailsTitle}
        toggler={() => setViewModalOpen(false)}
        hideSubmit={true}
        size="lg"
      >
        <div className="row">
          <InquiryViewField label={FullNameLabel} value={viewData?.fullName} />
          <InquiryViewField label={CompanyNameLabel} value={viewData?.companyName} />
          <InquiryViewField label={EmailLabel} value={viewData?.email} />
          <InquiryViewField label={StatusLabel} value={viewData?.status} />
          <InquiryViewField
            label={ReasonForContactLabel}
            value={viewData?.reasonForContact}
            col="col-md-12"
          />
          <InquiryViewField label={MessageLabel} value={viewData?.message} col="col-md-12" pre />
          <InquiryViewField label={RemarksLabel} value={viewData?.remarks} col="col-md-12" pre />
        </div>
      </BaseModal>

      <BaseModal
        isOpen={updateModalOpen}
        title={UpdateInquiryTitle}
        toggler={() => !updateLoading && setUpdateModalOpen(false)}
        submit={handleUpdateSubmit}
        loader={updateLoading}
        submitText={SubmitUpdateLabel}
        size="md"
        hideClose={updateLoading}
      >
        <div className="mb-3">
          <BaseSelect
            name="status"
            label={StatusLabel}
            value={updateForm.status}
            options={statusOptions}
            handleChange={(name, value) => setUpdateForm((p) => ({ ...p, [name]: value }))}
            required
          />
        </div>
        <div className="mb-3">
          <BaseInput
            name="remarks"
            label={RemarksLabel}
            type="textarea"
            value={updateForm.remarks}
            handleChange={(e) => setUpdateForm((p) => ({ ...p, remarks: e.target.value }))}
            rows="3"
          />
        </div>
      </BaseModal>
    </div>
  );
};

export default Inquiry;
